import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { ReverseByReferenceRequest } from "@/features/wallet/api/dto";

/**
 * Voie principale du worker ledger-service (`POST
 * /Transactions/reverse-by-reference`, guide v2.5 §6.3).
 *
 * Répercute une contre-passation comptable validée en maker-checker (docs/SI
 * §7) par référence — écriture compensatoire sur les soldes, transaction
 * passée en REVERSED. `reason` obligatoire. À chaîner derrière
 * `JournalEntries/{id}/reverse` approuvée en maker-checker.
 * Rôles : `flashpay-service`, `flashpay-superadmin`, `flashpay-admin`.
 */
export const useReverseByReference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "reverseByReference"],
    mutationFn: (body: ReverseByReferenceRequest) =>
      mutateData({
        url: walletEndpoints.transactionsExt.reverseByReference,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, walletTransactionCacheKeys()),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReverseByReference failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
